import { useState, useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import { commonAPI } from "../../../../services/CommonAPIService";
import { toast } from "react-toastify";
import CameraIcon from "../../../../assets/icons/maid-module-icons/Camera.svg";


const getDocType = (doc) => {
  const id    = (doc?.id    || "").toLowerCase();
  const label = (doc?.label || doc?.code || "").toLowerCase();
  if (id === "aadhaar" || label.includes("aadhaar") || label.includes("aadhar")) return "aadhaar";
  if (id === "pan"     || label.includes("pan"))                                  return "pan";
  if (label.includes("voter") || label.includes("epic"))                          return "voter";
  return "other";
};

const MASKED_PREFIX = {
  aadhaar : "**** **** ", 
  pan     : "****** ",   
  voter   : "****** ",   
  other   : "****** ",    
};

/** Labels, placeholders and max lengths per document type. */
const FIELD_CONFIG = {
  aadhaar : { label: "Aadhaar Number",   maskedLabel: "Last 4 digits of Aadhaar Number",   placeholder: "1234 5678 9012", maskedPlaceholder: "Enter last 4 digits",      maxLen: 12 },
  pan     : { label: "PAN Number",       maskedLabel: "Last 4 characters of PAN",           placeholder: "ABCDE1234F",    maskedPlaceholder: "Enter last 4 characters",   maxLen: 10 },
  voter   : { label: "Voter ID Number",  maskedLabel: "Last 4 characters of Voter ID",      placeholder: "ABC1234567",    maskedPlaceholder: "Enter last 4 characters",   maxLen: 10 },
  other   : { label: "Document Number",  maskedLabel: "Last 4 digits of Document Number",   placeholder: "Enter number",  maskedPlaceholder: "Enter last 4 digits",        maxLen: 20 },
};


const isValidMaskedChar = (char, docType, position) => {
  switch (docType) {
    case "aadhaar": return /\d/.test(char);
    case "pan":
      if (position < 3)  return /\d/.test(char);
      if (position === 3) return /[A-Z]/.test(char);
      return false;
    case "voter":   return /\d/.test(char);
    default:        return /\d/.test(char);
  }
};


const validateFullNumber = (num, docType) => {
  if (!num) return { valid: false, message: "Document number is required." };
  switch (docType) {
    case "aadhaar":
      if (!/^\d{12}$/.test(num))
        return { valid: false, message: "Aadhaar must be exactly 12 digits." };
      break;
    case "pan":
      
     if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(num))
        return { valid: false, message: "Invalid PAN. Format: ABCDE1234F (5 letters, 4 digits, 1 letter)." };
      break;
    case "voter":

      if (!/^[A-Z]{3}[0-9]{7}$/.test(num))
        return { valid: false, message: "Invalid Voter ID. Format: ABC1234567 (3 letters, 7 digits)." };
      break;
    default:
      if (num.length < 4)
        return { valid: false, message: "Document number must be at least 4 characters." };
  }
  return { valid: true, message: "" };
};


const validateMaskedNumber = (num, docType) => {
  if (!num) return { valid: false, message: "Document number is required." };
  if (num.length !== 4) return { valid: false, message: "Please enter exactly 4 characters." };
  switch (docType) {
    case "aadhaar":
      if (!/^\d{4}$/.test(num))
        return { valid: false, message: "Last 4 digits of Aadhaar must be numeric." };
      break;
    case "pan":
      // Last 4 of PAN: 3 digits + 1 uppercase letter  e.g. "234F"
      if (!/^\d{3}[A-Z]$/.test(num))
        return { valid: false, message: "Last 4 of PAN must be 3 digits then 1 letter. Example: 234F" };
      break;
    case "voter":
      // Last 4 of Voter ID: all digits  e.g. "4567"
      if (!/^\d{4}$/.test(num))
        return { valid: false, message: "Last 4 of Voter ID must be numeric." };
      break;
    default:
      if (!/^\d{4}$/.test(num))
        return { valid: false, message: "Last 4 digits must be numeric." };
  }
  return { valid: true, message: "" };
};

const parseMaskedSetting = (value) =>
  value === true || value === 1 || value === "1" || value === "true" ||
  String(value).toLowerCase() === "true";

const Identity = forwardRef(({ selectedDocument, setDocumentDetails }, ref) => {

  // Input state
  const [fullNumber,    setFullNumber]    = useState("");
  const [displayNumber, setDisplayNumber] = useState("");

  // Document image state
  const [preview, setPreview] = useState(null);
  const [file,    setFile]    = useState(null);

  // Camera state
  const [showCamera,  setShowCamera]  = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [videoReady,  setVideoReady]  = useState(false);
  const videoRef  = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  // Masked setting state
  const [showMasked,      setShowMasked]      = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(true);

  // Derived values — no extra state needed
  const docType = getDocType(selectedDocument);
  const prefix  = MASKED_PREFIX[docType];
  const config  = FIELD_CONFIG[docType];

  // ── Load masked setting once on mount ──
useEffect(() => {
  const loadSetting = async () => {
    const auth = localStorage.getItem("userData");
    if (!auth) {
      setShowMasked(false);
      setSettingsLoading(false);
      return;
    }

    let userData;

    try {
      userData = JSON.parse(auth);
    } catch (parseErr) {
      console.error("Invalid userData in localStorage", parseErr);
      setShowMasked(false);
      setSettingsLoading(false);
      return;
    }

    try {
      const response = await commonAPI("AppSettings", undefined, {
        urlVariable: null,
        buildingId: userData?.buildingId || null,
      });

      if (response?.success === true && Array.isArray(response.data)) {
        const setting = response.data.find(
          (item) => item.key === "SHOW_IDPROOF_MASKED"
        );
        setShowMasked(parseMaskedSetting(setting?.value));
      } else {
        setShowMasked(false);
      }
    } catch (err) {
      console.error("Error loading AppSettings:", err);

      const bodyError = {
        userId: userData?.userId,
        mobileNumber: userData?.mobileNumber,
        requestJSON: null,
        uri: "/api/Master/AppSettings",
        errorSource: "CuroNextPortal",
        errorNumber: "APPSET01",
        errorSummary: err?.message,
        errorDescription: err?.message,
        fileClassName: "SelectedDocument.js",
        functionName: "loadSetting",
      };

      try {
        await commonAPI("ErrorLogMaster", bodyError);
      } catch (logErr) {
        console.error("Error while logging error:", logErr);
      }

      setShowMasked(false);
    } finally {
      setSettingsLoading(false);
    }
  };

  loadSetting();
}, []);

  useEffect(() => {
    setFullNumber("");
    setDisplayNumber(!settingsLoading && showMasked ? prefix : "");
  }, [selectedDocument, showMasked, settingsLoading]);

  //  Cleanup on unmount 
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
      stopCamera();
    };
  }, [preview]);

  //  Camera helpers 
  const stopCamera = () => {
    streamRef.current?.getTracks().forEach(track => track.stop());
    streamRef.current = null;
    setVideoReady(false);
  };

  const startCamera = async () => {
    setCameraError(null);
    setVideoReady(false);
    stopCamera();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video : { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio : false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject   = stream;
        videoRef.current.playsInline = true;
        await new Promise((resolve, reject) => {
          videoRef.current.onloadedmetadata = () => videoRef.current.play().catch(() => {});
          videoRef.current.oncanplay        = () => { setVideoReady(true); resolve(); };
          videoRef.current.onerror          = reject;
       
          setTimeout(() => {
            if (videoRef.current?.readyState >= 2) { setVideoReady(true); resolve(); }
          }, 4000);
        });
      }
      setShowCamera(true);
    } catch (err) {
      const msgs = {
        NotAllowedError : "Camera permission denied. Please allow camera access.",
        NotFoundError: "No camera found on this device.",
        NotReadableError: "Camera is already in use by another application.",
      };
      setCameraError(msgs[err.name] || "Cannot access camera. Please try uploading instead.");
      setShowCamera(false);
    }
  };

  const captureFromCamera = () => {
    if (!videoRef.current || !canvasRef.current || !videoReady) {
      alert("Camera is not ready yet. Please wait a moment.");
      return;
    }
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width  = video.videoWidth  || 1280;
    canvas.height = video.videoHeight || 720;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
    fetch(dataUrl)
      .then(r => r.blob())
      .then(blob => {
        const capturedFile = new File([blob], `capture-${Date.now()}.jpg`, { type: "image/jpeg" });
        setFile(capturedFile);
        setPreview(dataUrl);
        setDocumentDetails({ type: selectedDocument, number: fullNumber, file: capturedFile, isMasked: showMasked });
        stopCamera();
        setShowCamera(false);
      })
      .catch(() => alert("Failed to process the captured image. Please try again."));
  };

  // File upload 
  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    if (!selectedFile.type.startsWith("image/")) { alert("Please select a valid image file (JPG or PNG)."); return; }
    if (selectedFile.size > 5 * 1024 * 1024)     { alert("File size must be under 5MB."); return; }
    const url = URL.createObjectURL(selectedFile);
    setPreview(url);
    setFile(selectedFile);
    setDocumentDetails({ type: selectedDocument, number: fullNumber, file: selectedFile, isMasked: showMasked });
    stopCamera();
    setShowCamera(false);
  };

  // ── Number input — unmasked mode ───
  const handleNumberChange = (e) => {
    if (showMasked) return; 

    let value = e.target.value;
    if      (docType === "aadhaar") value = value.replace(/\D/g, "").slice(0, 12);
    else if (docType === "pan")     value = value.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 10);
    else if (docType === "voter")   value = value.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 10);
    else                            value = value.slice(0, 20);

    setFullNumber(value);
    setDisplayNumber(value);
    setDocumentDetails(prev => ({ ...prev, type: selectedDocument, number: value, file, isMasked: false }));
  };

  const handleKeyDown = (e) => {
    if (!showMasked) return;
    e.preventDefault();

    if (e.key === "Backspace") {
      const next = fullNumber.slice(0, -1);
      setFullNumber(next);
      setDisplayNumber(prefix + next);
      setDocumentDetails(prev => ({ ...prev, number: next, isMasked: true }));
      return;
    }

    if (e.key.length !== 1)    return; // ignore non-printable keys
    if (fullNumber.length >= 4) return; // already at max

    const char     = e.key.toUpperCase();
    const position = fullNumber.length; 

    if (!isValidMaskedChar(char, docType, position)) return;

    const next = fullNumber + char;
    setFullNumber(next);
    setDisplayNumber(prefix + next);
    setDocumentDetails(prev => ({ ...prev, number: next, isMasked: true }));
  };

  //  Validation
  const validate = () =>
    showMasked
      ? validateMaskedNumber(fullNumber.trim(), docType)
      : validateFullNumber(fullNumber.trim(), docType);

  useImperativeHandle(ref, () => ({
    validateBeforeContinue: () => {
      const { valid, message } = validate();
      if (!valid) { toast.warning(message); return false; }
      if (!file)  { toast.warning("Please upload or capture a document photo."); return false; }
      return true;
    },
  }));

  // ── Derived display values ─
  const fieldLabel       = showMasked ? config.maskedLabel       : config.label;
  const fieldPlaceholder = showMasked ? config.maskedPlaceholder : config.placeholder;
  const inputMaxLength   = showMasked ? prefix.length + 4 : config.maxLen;

 
  // Render

  return (
    <div className="position-relative">

      {/* Selected document type display */}
      <div className="d-flex justify-content-center mb-3">
        <label className="form-label fw-bold mb-2">Document Type Selected</label>
      </div>

      <div
        className="d-flex align-items-center p-3 mb-4 shadow-sm rounded border"
        style={{ background: "#FFFFFF", border: "1px solid #DBDBDB" }}
      >
        {selectedDocument?.image && (
          <img
            src={selectedDocument.image}
            alt={selectedDocument.label}
            style={{ width: 48, height: 48, objectFit: "contain", marginRight: 16 }}
          />
        )}
        <div style={{ fontSize: 17, fontWeight: 500 }}>
          {selectedDocument?.label || "Selected Document"}
        </div>
      </div>

      {/* Document number input */}
      <div style={{ marginTop: "10px" }}>
        <label className="form-label fw-bold mb-2">{fieldLabel}</label>
      </div>

      <input
        type="text"
        className="form-control"
        value={displayNumber}
        onChange={handleNumberChange}
        onKeyDown={handleKeyDown}
        onPaste={(e) => { if (showMasked) e.preventDefault(); }}
        onCut={(e)   => { if (showMasked) e.preventDefault(); }}
        // Keep cursor at end in masked mode — prevents typing inside the prefix
        onFocus={(e) => { if (showMasked) setTimeout(() => e.target.setSelectionRange(e.target.value.length, e.target.value.length), 0); }}
        onClick={(e) => { if (showMasked) setTimeout(() => e.target.setSelectionRange(e.target.value.length, e.target.value.length), 0); }}
        placeholder={fieldPlaceholder}
        maxLength={inputMaxLength}
        style={{
          fontSize      : 25,
          fontFamily    : "monospace",
          height        : "40px",
          letterSpacing : showMasked ? "0.2em" : docType === "aadhaar" ? "0.35em" : "normal",
          textTransform : !showMasked && (docType === "pan" || docType === "voter") ? "uppercase" : "none",
        }}
      />

      {/* Document photo */}
      <div className="mb-4">
        <div style={{ marginTop: "15px" }}>
          <label className="form-label fw-bold mb-2">Document Photo</label>
        </div>

        <div
          style={{
            border       : preview ? "2px solid #198754" : "2px dashed #2295D2",
            borderRadius : 12,
            minHeight    : 240,
            background   : preview ? "#f0fff4" : "#f8f9fa",
            position     : "relative",
            overflow     : "hidden",
            cursor       : preview ? "default" : "pointer",
          }}
        >
          {preview ? (
            <img
              src={preview}
              alt="Document preview"
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          ) : (
            <div style={{ height: "250px", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div
                className="d-flex flex-column align-items-center justify-content-center text-center"
                onClick={() => document.getElementById("fileInput")?.click()}
                style={{ cursor: "pointer" }}
              >
                <img src={CameraIcon} alt="Camera" style={{ width: "25px", height: "25px", marginBottom: "10px" }} />
                <div style={{ fontSize: 20, fontWeight: 600, color: "#2295D2" }}>
                  Click to upload or capture photo
                </div>
                <div style={{ fontSize: 14, color: "#6c757d" }}>JPG, PNG • Max 5MB</div>
              </div>
            </div>
          )}

          {/* Invisible file input covers the entire upload box */}
          <input
            id="fileInput"
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileChange}
            style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer", zIndex: 5 }}
          />
        </div>

        {cameraError && !showCamera && (
          <div className="alert alert-warning mt-2 small">{cameraError}</div>
        )}

        {preview && (
          <div className="text-center mt-3">
            <button
              className="btn btn-outline-secondary btn-sm px-4"
              onClick={() => {
                setPreview(null);
                setFile(null);
                setDocumentDetails(prev => ({ ...prev, file: null }));
              }}
            >
              Retake / Replace Photo
            </button>
          </div>
        )}
      </div>

      {/* Full-screen camera overlay */}
      {showCamera && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 2000, display: "flex", flexDirection: "column" }}>
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{ width: "100%", maxHeight: "75vh", objectFit: "cover", borderRadius: 12, background: "#000" }}
            />
            <canvas ref={canvasRef} style={{ display: "none" }} />
          </div>
          <div style={{ padding: "20px", background: "#111", display: "flex", justifyContent: "center", gap: 32 }}>
            <button
              onClick={captureFromCamera}
              className={`btn btn-lg px-5 ${videoReady ? "btn-success" : "btn-secondary"}`}
              disabled={!videoReady}
              style={{ borderRadius: 50 }}
            >
              {videoReady ? "Capture" : "Preparing camera..."}
            </button>
            <button
              onClick={() => { stopCamera(); setShowCamera(false); }}
              className="btn btn-outline-light px-5"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

    </div>
  );
});

export default Identity;